import {TrimPoint} from "../types/editor";
import {Clip, Highlight, MamClipElement} from "../types/clipTypes";
import {deleteHighlights, deleteSocialClips} from "../queries/mamPageQueries";
import {DataJob} from "../types/jobDataTypes";
import {Competition} from "../types/competitionTypes";

export const formatLabel = (str: string) => {
    const spacedString = str
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ');
    return spacedString.charAt(0).toUpperCase() + spacedString.slice(1);
};

export function formatDuration(minutes: number) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.floor(minutes % 60);

    const formattedMinutes = String(hours).padStart(2, '0');
    const formattedSeconds = String(remainingMinutes).padStart(2, '0');

    return `${formattedMinutes}:${formattedSeconds}`;
}

export function formatProductionId(productionId: string | null): string {
    if (!productionId) {
        return 'None';
    }
    const year = productionId.substring(0, 4);
    const month = productionId.substring(4, 6);
    const day = productionId.substring(6, 8);
    const hour = productionId.substring(8, 10);
    const minute = productionId.substring(10, 12);

    return `${year}-${month}-${day} ${hour}:${minute}`;
}

export function formatJobExtraction(jobExtractionTime: string | null | undefined): string {
    if (!jobExtractionTime) {
        return 'None';
    }
    const date = new Date(jobExtractionTime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return`${year}-${month}-${day} ${hour}:${minute}`;
}

function magneticEffect(time: number, diff: number, shots: number[]){
    for (const shot of shots){
        if(Math.abs(time - shot) < diff){
            return shot
        }
    }
    return -1;
}

export function formatTime(totalHours: number, totalMInutes: number): string {
    // Aggiungi i secondi ai minuti per avere i secondi totali
    const totalTimeInMinutes = totalHours * 60 + totalMInutes;

    // Calcola le ore, minuti e secondi
    const hours = Math.floor(totalTimeInMinutes/ 60);
    const minutes = Math.floor((totalTimeInMinutes % 60) );

    // Format the time as HH:MM:SS
    return String(hours).padStart(2, '0') + ':' +
        String(minutes).padStart(2, '0');
}

export async function download(videoUrl: string, fileName: string, aspect_ratio: string){
    try {
        const cleanUrl = videoUrl.split('?')[0];
        const extension = cleanUrl.substring(cleanUrl.lastIndexOf('.') + 1);
        fileName = fileName + "_" + aspect_ratio + '.' + extension;
        const response = await fetch(videoUrl);
        if (!response.ok) {
            throw new Error("Failed to fetch the video");
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName; // Adjust filename and extension accordingly
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error downloading video:", error);
    }
}

export function moveTrimPoint(
    trimPoints: TrimPoint[],
    seek: number,
    totalVideoTime: number,
    index: number,
    type: 'start' | 'end',
    deleteTrimPoint: (index: number) => void,
    updateTrimPoints: (trimPoints: TrimPoint[], time: number) => void,
    mergeTrimPoints: (index1 :number, index2: number) => void,
    magnetic: {shots: number[], use: boolean} = {shots: [], use: false},
){
        const prevEndGrabber = index - 1 >= 0 ? trimPoints[index - 1] : null
        const nextStartGrabber = index + 1 < trimPoints.length ? trimPoints[index + 1] : null
        const isStartGrabber = type === 'start';
        const beforeStart = seek < 0;
        const afterEnd = seek > totalVideoTime;
        const beforePrevEnd = prevEndGrabber ? seek <= prevEndGrabber.end : false;
        const afterNextStart = nextStartGrabber ? seek >= nextStartGrabber.start : false;
        const beforeCurrentStart = seek <= trimPoints[index].start;
        const afterCurrentEnd = seek >= trimPoints[index].end;
        const magneticValue = magneticEffect(seek, 10, magnetic.shots)
        const useMagnetic = magnetic.use && magneticValue >= 0
        /*console.log({
            index: index,
            isStartGrabber: type === 'start',
            beforeStart: seek < 0,
            afterEnd: seek > totalVideoTime,
            beforePrevEnd: prevEndGrabber ? seek <= prevEndGrabber.end : false,
            afterNextStart: nextStartGrabber ? seek >= nextStartGrabber.start : false,
            beforeCurrentStart: seek <= trimPoints[index].start,
            afterCurrentEnd: seek >= trimPoints[index].end
        });*/
        if (isStartGrabber && afterCurrentEnd) {
            deleteTrimPoint(index)
        } else if(isStartGrabber && beforePrevEnd){
            mergeTrimPoints(index - 1, index)
        } else if (isStartGrabber && !afterCurrentEnd && !beforePrevEnd && !beforeStart) {
            const updateValue = useMagnetic ? magneticValue : seek;
            trimPoints[index][type] = updateValue
            updateTrimPoints([...trimPoints], updateValue);
        }
        if (!isStartGrabber && beforeCurrentStart) {
            deleteTrimPoint(index)
        } else if (!isStartGrabber && afterNextStart){
            mergeTrimPoints(index, index + 1)
        } else if (!isStartGrabber && !beforeCurrentStart && !afterNextStart && !afterEnd) {
            const updateValue = useMagnetic ? magneticValue : seek;
            trimPoints[index][type] = updateValue
            updateTrimPoints([...trimPoints], updateValue);
        }
}

export function findClipDescription(clip: Highlight | Clip): string{
    const descrObj = clip.descriptions.find(descr => descr.style === 'base_description');
    return descrObj ? descrObj.description : '';
}

export function formatDateFromString(dateString: string): string {
    // Crea un oggetto Date dalla stringa
    const date = new Date(dateString);

    // Verifica che la data sia valida
    if (isNaN(date.getTime())) {
        return 'None';
    }

    // Ottieni giorno, mese, anno, ore e minuti
    const day = date.getDate().toString().padStart(2, '0'); // Aggiunge 0 se il giorno è a una cifra
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // I mesi in JS partono da 0
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    // Restituisci la data nel formato desiderato
    return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export const handleDeleteSocialClips = async (ids: string[], setSocialClips: any, type: string, abortControllerRef: any, checkedItems?: any, setCheckedItems?: any) => {
    let previousClips: MamClipElement[] = [];

    if(abortControllerRef?.current) {
        abortControllerRef?.current.abort();
    }

    // 2) Remove items from local state immediately
    setSocialClips((prev: MamClipElement[]) => {
        previousClips = prev; // Keep a copy for potential revert
        return prev.filter((clip: any) => {
            const clipId = clip.type === "clip" ? clip.social_clip_id : clip.id;
            return !ids.includes(String(clipId));
        });
    });

    // If we also maintain a "checkedItems" array, clear or adjust that
    if (checkedItems && setCheckedItems) {
        setCheckedItems((prev: any) =>
            prev.filter((item: any) => {
                const itemId = item.type === "clip" ? item.social_clip_id : item.id;
                return !ids.includes(String(itemId));
            })
        );
    }

    try {
        if(type === "highlight") {
            await deleteHighlights(ids);
        } else {
            await deleteSocialClips(ids);
        }
        if(checkedItems) {
            setSocialClips((prevMamData: any) =>
                prevMamData.filter(
                    (clip: any) =>
                        // Keep clips that weren't checked
                        !checkedItems.some(
                            (item: any) => clip.type === "clip" ? (item.social_clip_id === clip.social_clip_id) : (item.id === clip.id)
                        )
                )
            );
            setCheckedItems([]);
        } else {
            setSocialClips((prevMamData: any) =>
                prevMamData.filter(
                    (clip: any) => !ids.includes(String(clip.type === "clip" ? clip.social_clip_id: clip.id))
                )
            );
        }
    } catch (error) {
        console.error("Failed to delete clips: ", error);
        setSocialClips(previousClips);
    }
}

export function getClipTrimDuration(trimPoints: TrimPoint[], clip: Clip | Highlight): number {
    let cutDuration = 0
    for(const trimPoint of trimPoints){
        cutDuration += trimPoint.end - trimPoint.start
    }
    return trimPoints.length > 0 ? cutDuration : clip.duration;
}

export function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

type GenericObject = { [key: string]: any }; // Generic object type

export function mergeArraysWithoutDuplicates<T extends GenericObject>(
    array1: T[],
    array2: T[],
    field: keyof T
): T[] {
    const existingValues = new Set(array1.map(item => item[field]));
    return [
        ...array1,
        ...array2.filter(item => !existingValues.has(item[field]))
    ];
}

export const createJobObject = (data: DataJob) => {
    const filteredData = data.settings.events_of_interest.filter(item => item.included !== false);
    const tempSkills = data.settings.skills.filter(item => item.included !== false)
    const tempSettings = { ...data.settings };

    for (let i = 0; i < filteredData.length; i++) {
        if (filteredData[i].start === -31 && filteredData[i].end === 31) {
            filteredData[i] = {
                start: "auto",
                end: "auto",
                replay: filteredData[i].replay,
                key_moment: filteredData[i].key_moment,
                var: filteredData[i].var,
                auto_publication : filteredData[i].auto_publication
            }
        } else if (filteredData[i].end === 31) {
            filteredData[i] = {
                start: filteredData[i].start,
                end: "auto",
                replay: filteredData[i].replay,
                key_moment: filteredData[i].key_moment,
                var: filteredData[i].var,
                auto_publication : filteredData[i].auto_publication
            }
        } else if (filteredData[i].start === -31) {
            filteredData[i] = {
                start: "auto",
                end: filteredData[i].end,
                replay: filteredData[i].replay,
                key_moment: filteredData[i].key_moment,
                var: filteredData[i].var,
                auto_publication : filteredData[i].auto_publication
            }
        } else {
            filteredData[i] = {
                start: filteredData[i].start,
                end: filteredData[i].end,
                replay: filteredData[i].replay,
                key_moment: filteredData[i].key_moment,
                var: filteredData[i].var,
                auto_publication : filteredData[i].auto_publication
            }
        }
    }

    for (let i=0; i<tempSkills.length; i++) {
        if (tempSkills[i].start === -31 && tempSkills[i].end === 31) {
            tempSkills[i] = {
                start: "auto",
                end: "auto",
                replay: tempSkills[i].replay,
                key_moment: tempSkills[i].key_moment,
                var: tempSkills[i].var,
                auto_publication : tempSkills[i].auto_publication
            }
        } else if (tempSkills[i].end === 31) {
            tempSkills[i] = {
                start: tempSkills[i].start,
                end: "auto",
                replay: tempSkills[i].replay,
                key_moment: tempSkills[i].key_moment,
                var: tempSkills[i].var,
                auto_publication : tempSkills[i].auto_publication
            }
        } else if (tempSkills[i].start === -31) {
            tempSkills[i] = {
                start: "auto",
                end: tempSkills[i].end,
                replay: tempSkills[i].replay,
                key_moment: tempSkills[i].key_moment,
                var: tempSkills[i].var,
                auto_publication : tempSkills[i].auto_publication
            }
        } else {
            tempSkills[i] = {
                start: tempSkills[i].start,
                end: tempSkills[i].end,
                replay: tempSkills[i].replay,
                key_moment: tempSkills[i].key_moment,
                var: tempSkills[i].var,
                auto_publication : tempSkills[i].auto_publication
            }
        }
    }

    tempSettings.events_of_interest = filteredData;
    tempSettings.skills = tempSkills

    return tempSettings
}

export function getNumericAspectRatio(aspectRatio: string): number {
    return parseInt(aspectRatio.split(":")[0])/parseInt(aspectRatio.split(":")[1])
}

export function getEditCoordinates(trimPoints: TrimPoint[], coordinates: number[], frameRate: number): number []{
    if(!trimPoints || trimPoints.length === 0) {
        return coordinates;
    }
    if (!coordinates){
        return [];
    }
    let editCoordinates: number[] = []
    for (const trimPoint of trimPoints){
        editCoordinates = editCoordinates.concat(coordinates.slice(trimPoint.start * frameRate, trimPoint.end * frameRate))
    }
    return editCoordinates
}

export function getUniqueCompetitions(selectedItems: any[]) {
    const uniqueCompetitionsId = new Set<string>();
    const uniqueCompetitions: Competition[] = [];
    for(const selectedItem of selectedItems) {
        if(selectedItem.type === "highlight") {
            const selectedItemCompetitionId = selectedItem.competition_id;
            const selectedItemCompetitionName = selectedItem.competition;
            const splittedCompetitionsNames = selectedItemCompetitionName.split("/").map((competitionName: string) => competitionName.trim());
            const splittedCompetitionsIds = selectedItemCompetitionId.split("/").map((competition: string) => competition.trim());
            for(let i = 0; i < splittedCompetitionsIds.length; i++) {
                if(!uniqueCompetitionsId.has(splittedCompetitionsIds[i])) {
                    uniqueCompetitions.push({id: splittedCompetitionsIds[i], name: splittedCompetitionsNames[i], type: "", season: ""})
                    uniqueCompetitionsId.add(splittedCompetitionsIds[i])
                }
            }
        } else if(selectedItem.type === "clip") {
            if(!uniqueCompetitionsId.has(selectedItem.competition_id)) {
                uniqueCompetitions.push({id: selectedItem.competition_id, name: selectedItem.competition, type: "", season: ""})
                uniqueCompetitionsId.add(selectedItem.competition_id);
            }
        }
    }
    return uniqueCompetitions;
}

export function areSelectedItemsMultiEvent(selectedItems: any[]) {
    if (selectedItems.length === 0) return false;

    // Collect all event IDs from selected items
    const allEventIds = selectedItems.flatMap(item => {
        if (item.type === "highlight") {
            return item.event_id.split("/").map((id: string) => id.trim());
        } else {
            return [item.event_id];
        }
    });

    // Create a Set of unique event IDs
    const uniqueEventIds = new Set(allEventIds);

    // If there's more than one unique event ID, return true
    return uniqueEventIds.size > 1;
}